/**
 * Reference-number ("Reference #", "RFI #", "Item #", "Package #", …) suggestion for
 * document creation.
 *
 * The rule mirrors the backend's `computeNextReferenceNumbers`
 * (`tsoa-src/design-meeting-minutes/design-meeting-minutes-utils.ts`), which numbers Tasks
 * published from Meeting Minutes: continue from the largest existing numeric
 * referenceNumber, carrying the zero-padding of the widest existing value ("007" → "008"),
 * widening naturally when the next number outgrows it ("099" → "100"). Non-numeric
 * reference numbers are ignored so a manually entered label never blocks autonumbering, and
 * when nothing numeric exists yet numbering starts at 1 with no padding.
 *
 * Kept dependency-free so it can be unit tested without pulling in the API client.
 */
export function computeNextReferenceNumbers(
  existingReferenceNumbers: (string | null | undefined)[],
  count: number,
): string[] {
  let maxValue = 0;
  let padWidth = 0;
  let hasNumeric = false;
  for (const raw of existingReferenceNumbers) {
    if (!raw) continue;
    const trimmed = raw.trim();
    if (!/^\d+$/.test(trimmed)) continue;
    const value = parseInt(trimmed, 10);
    if (!hasNumeric || value > maxValue || (value === maxValue && trimmed.length > padWidth)) {
      maxValue = value;
      padWidth = trimmed.length;
    }
    hasNumeric = true;
  }

  const start = hasNumeric ? maxValue + 1 : 1;
  const result: string[] = [];
  for (let i = 0; i < count; i += 1) {
    result.push(String(start + i).padStart(padWidth, '0'));
  }
  return result;
}

/** Single-document convenience wrapper around {@link computeNextReferenceNumbers}. */
export function computeNextReferenceNumber(
  existingReferenceNumbers: (string | null | undefined)[],
): string {
  return computeNextReferenceNumbers(existingReferenceNumbers, 1)[0];
}
